import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";

const USE_MOCK_DATA = false;

/* ─── Constants ─── */

const DELIVERY_MODES = [
    { value: "svd", label: "SVD — Spontaneous Vaginal Delivery" },
    { value: "vacuum", label: "Vacuum-Assisted Delivery" },
    { value: "forceps", label: "Forceps-Assisted Delivery" },
    { value: "emergency_cs", label: "Emergency Caesarean Section" },
    { value: "elective_cs", label: "Elective Caesarean Section" },
    { value: "breech_delivery", label: "Assisted Breech Delivery" },
];

const PLACENTA_METHODS = [
    { value: "cct", label: "CCT — Controlled Cord Traction" },
    { value: "manual_removal", label: "Manual Removal of Placenta" },
];

const EPISIOTOMY_TYPES = [
    { value: "none", label: "None" },
    { value: "mediolateral", label: "Mediolateral" },
    { value: "midline", label: "Midline" },
];

const TEAR_DEGREES = [
    { value: "none", label: "None" },
    { value: "1", label: "1st Degree" },
    { value: "2", label: "2nd Degree" },
    { value: "3a", label: "3a — <50% external sphincter" },
    { value: "3b", label: "3b — >50% external sphincter" },
    { value: "3c", label: "3c — Internal sphincter involved" },
    { value: "4", label: "4th Degree — Rectal mucosa" },
];

const UTEROTONICS = [
    { value: "oxytocin", label: "Oxytocin 10 IU IM" },
    { value: "misoprostol", label: "Misoprostol 600mcg PO" },
    { value: "ergometrine", label: "Ergometrine 0.5mg IM" },
    { value: "carbetocin", label: "Carbetocin 100mcg IV" },
];

const CORD_CLAMPING = [
    { value: "delayed", label: "Delayed (1-3 min)" },
    { value: "early", label: "Early (<1 min)" },
];

const MATERNAL_CONDITIONS = [
    { value: "stable", label: "Stable" },
    { value: "unstable", label: "Unstable" },
    { value: "critical", label: "Critical" },
];

const COMPLICATION_OPTIONS = [
    { value: "pph", label: "Postpartum Haemorrhage (PPH)" },
    { value: "retained_placenta", label: "Retained Placenta" },
    { value: "uterine_atony", label: "Uterine Atony" },
    { value: "cervical_tear", label: "Cervical Tear" },
    { value: "vaginal_tear", label: "Vaginal Wall Tear" },
    { value: "uterine_rupture", label: "Uterine Rupture" },
    { value: "cord_prolapse", label: "Cord Prolapse" },
    { value: "shoulder_dystocia", label: "Shoulder Dystocia" },
    { value: "eclampsia", label: "Eclampsia" },
    { value: "amniotic_embolism", label: "Amniotic Fluid Embolism" },
    { value: "inverted_uterus", label: "Inverted Uterus" },
    { value: "none", label: "No Complications" },
];

/* ─── Helper ─── */
const nowLocal = function() {
    const d = new Date();
    const pad = function(n) { return n < 10 ? "0" + n : "" + n; };
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) +
        "T" + pad(d.getHours()) + ":" + pad(d.getMinutes());
};

/* ─── Initial Form State ─── */
const INITIAL_FORM = {
    delivery_datetime: nowLocal(),
    delivery_mode: "",
    indication_operative: "",
    duration_first_stage_hours: "",
    duration_second_stage_minutes: "",
    duration_third_stage_minutes: "",
    placenta_complete: true,
    placenta_delivery_method: "cct",
    estimated_blood_loss_ml: "",
    episiotomy: "none",
    perineal_tear_degree: "none",
    perineal_repair: false,
    uterotonic_given: "oxytocin",
    uterotonic_time: "",
    cord_clamping: "delayed",
    amtsl_completed: false,
    complications: [],
    maternal_condition_post_delivery: "stable",
    number_of_babies: "1",
    notes: "",
};

/* ─── AMTSL Checklist Items ─── */
const AMTSL_ITEMS = [
    { key: "uterotonic", label: "Uterotonic given within 1 minute of delivery" },
    { key: "cct", label: "Controlled cord traction performed" },
    { key: "uterine_massage", label: "Uterine massage after placental delivery" },
];

/* ═══ MOCK DATA ═══ */

const MOCK_ADMISSION = {
    id: 9001,
    patient_number: "MOCK-PT-001",
    visit_id: 5001,
    booking_id: 3001,
    gestational_age_weeks: 39,
    onset_type: "spontaneous",
    fetal_presentation: "cephalic",
    status: "in_labour",
};

const MOCK_BOOKING = {
    id: 3001,
    patient_number: "MOCK-PT-001",
    lnmp: "2025-06-01",
    edd: "2026-03-08",
    gravida: 2,
    parity: 1,
    blood_group: "O+",
    status: "active",
};

/* Mock an existing delivery record (null = not yet recorded) */
const MOCK_DELIVERY = null;

/* ─── Admission helpers ─── */
const findPatientAdmission = function(admissions, patient_number) {
    if (!Array.isArray(admissions) || !patient_number) return null;
    const matches = admissions.filter(function(item) {
        return item.patient_number === patient_number;
    });
    if (!matches.length) return null;
    matches.sort(function(a, b) {
        const aTime = a.admission_date ? new Date(a.admission_date).getTime() : 0;
        const bTime = b.admission_date ? new Date(b.admission_date).getTime() : 0;
        return bTime - aTime;
    });
    const active = matches.find(function(item) {
        return item.status !== 3;
    });
    return active || matches[0];
};

/* ═══ HOOK ═══ */
export const useDeliveryRecord = function(actions, patient_number, visit_id) {

    /* ─── Labour admission ─── */
    const admState = useState(null);
    const admission = admState[0];
    const setAdmission = admState[1];

    /* ─── Booking ─── */
    const bookState = useState(null);
    const booking = bookState[0];
    const setBooking = bookState[1];

    /* ─── Existing delivery record ─── */
    const delState = useState(null);
    const deliveryRecord = delState[0];
    const setDeliveryRecord = delState[1];

    /* ─── Form state ─── */
    const formState = useState(Object.assign({}, INITIAL_FORM));
    const form = formState[0];
    const setForm = formState[1];

    /* ─── AMTSL checklist ─── */
    const amtslState = useState({ uterotonic: false, cct: false, uterine_massage: false });
    const amtslChecklist = amtslState[0];
    const setAmtslChecklist = amtslState[1];

    /* ─── Newborn records linked to this delivery ─── */
    const nbState = useState([]);
    const newborns = nbState[0];
    const setNewborns = nbState[1];

    /* ─── UI state ─── */
    const loadState = useState(true);
    const loading = loadState[0];
    const setLoading = loadState[1];

    const savingState = useState(false);
    const saving = savingState[0];
    const setSaving = savingState[1];

    const tabState = useState("delivery");
    const activeTab = tabState[0];
    const setActiveTab = tabState[1];

    const sectionState = useState("mode");
    const activeSection = sectionState[0];
    const setActiveSection = sectionState[1];

    /* ─── Fetch admission + booking ─── */
    useEffect(function() {
        if (!patient_number || !visit_id) return;
        setLoading(true);
        if (USE_MOCK_DATA) {
            setTimeout(function() {
                setAdmission(MOCK_ADMISSION);
                setBooking(MOCK_BOOKING);
                if (MOCK_DELIVERY) {
                    setDeliveryRecord(MOCK_DELIVERY);
                    /* Pre-fill form from existing record */
                    setForm(Object.assign({}, INITIAL_FORM, MOCK_DELIVERY));
                }
                setLoading(false);
            }, 400);
            return;
        }

        const getAdmissionByPatientAndVisit = function() {
            return axios.get(config.smsUrl + "/obgyn/labour/admission?patient_number=" + patient_number + "&visit_id=" + visit_id)
                .then(function(res) { return res.data || {}; })
                .catch(function() { return {}; });
        };

        const getAdmissionByVisit = function() {
            return axios.get(config.smsUrl + "/obgyn/labour/admission-by-visit/" + visit_id)
                .then(function(res) { return res.data || {}; })
                .catch(function() { return {}; });
        };

        const getAdmissionFromPatientHistory = function() {
            return axios.get(config.smsUrl + "/obgyn/labour/admissions")
                .then(function(res) {
                    return findPatientAdmission(res.data.admissions || [], patient_number) || {};
                })
                .catch(function() { return {}; });
        };

        const getBooking = function() {
            return axios.get(config.smsUrl + "/obgyn/anc/booking/" + patient_number)
                .then(function(res) { return res.data || {}; })
                .catch(function() { return {}; });
        };

        Promise.all([getAdmissionByPatientAndVisit(), getAdmissionByVisit(), getAdmissionFromPatientHistory(), getBooking()])
            .then(function(results) {
                const admData = results.find(function(item) { return item && item.id; }) || {};
                const bookData = results[3] || {};
                if (admData.id) setAdmission(admData);
                if (bookData.id) setBooking(bookData);
                if (admData.id) {
                    return axios.get(config.smsUrl + "/obgyn/delivery/by-admission/" + admData.id);
                }
                return null;
            }).then(function(res) {
                if (res && res.data && res.data.id) {
                    setDeliveryRecord(res.data);
                    setForm(Object.assign({}, INITIAL_FORM, res.data));
                }
            }).catch(function() {}).finally(function() { setLoading(false); });
    }, [patient_number, visit_id]);

    /* ─── Form change handler ─── */
    const handleChange = useCallback(function(e) {
        const name = e.target.name;
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm(function(prev) {
            const next = Object.assign({}, prev);
            next[name] = value;
            return next;
        });
    }, []);

    /* ─── Complication toggle ─── */
    const toggleComplication = useCallback(function(compValue) {
        setForm(function(prev) {
            const current = prev.complications || [];
            const idx = current.indexOf(compValue);
            let next;
            if (idx >= 0) {
                next = current.filter(function(c) { return c !== compValue; });
            } else {
                next = current.concat([compValue]);
            }
            return Object.assign({}, prev, { complications: next });
        });
    }, []);

    /* ─── AMTSL toggle ─── */
    const toggleAmtsl = useCallback(function(key) {
        setAmtslChecklist(function(prev) {
            const next = Object.assign({}, prev);
            next[key] = !next[key];
            return next;
        });
    }, []);

    /* ─── Compute AMTSL completed ─── */
    const amtslComplete = useMemo(function() {
        return amtslChecklist.uterotonic && amtslChecklist.cct && amtslChecklist.uterine_massage;
    }, [amtslChecklist]);

    /* ─── PPH Alert ─── */
    const pphAlert = useMemo(function() {
        const ebl = parseFloat(form.estimated_blood_loss_ml);
        if (!ebl) return null;
        const isCS = form.delivery_mode === "emergency_cs" || form.delivery_mode === "elective_cs";
        const threshold = isCS ? 1000 : 500;
        if (ebl >= threshold) {
            return {
                type: ebl >= (isCS ? 2000 : 1000) ? "critical" : "warning",
                title: ebl >= (isCS ? 2000 : 1000) ? "Major PPH" : "PPH Alert",
                detail: "Estimated blood loss " + ebl + "ml (threshold " + threshold + "ml for " +
                    (isCS ? "C-section" : "vaginal delivery") + ")",
                action: ebl >= (isCS ? 2000 : 1000)
                    ? "URGENT: Activate massive transfusion protocol. Call for help."
                    : "Monitor closely. Prepare uterotonics. Type and screen.",
            };
        }
        return null;
    }, [form.estimated_blood_loss_ml, form.delivery_mode]);

    /* ─── Alerts ─── */
    const alerts = useMemo(function() {
        const list = [];
        if (pphAlert) list.push(pphAlert);

        /* Maternal condition */
        if (form.maternal_condition_post_delivery === "critical") {
            list.push({
                type: "critical",
                title: "Critical Maternal Condition",
                detail: "Mother in critical condition post-delivery",
                action: "Activate obstetric emergency team. Notify consultant.",
            });
        } else if (form.maternal_condition_post_delivery === "unstable") {
            list.push({
                type: "warning",
                title: "Unstable Maternal Condition",
                detail: "Mother unstable post-delivery",
                action: "Close monitoring. Assess vitals q15min. Prepare for intervention.",
            });
        }

        /* Retained placenta */
        if (!form.placenta_complete) {
            list.push({
                type: "warning",
                title: "Incomplete Placenta",
                detail: "Placenta not delivered complete — risk of retained products",
                action: "Manual exploration. Consider ultrasound. Monitor for PPH.",
            });
        }

        /* Severe tear */
        if (form.perineal_tear_degree === "3a" || form.perineal_tear_degree === "3b" ||
            form.perineal_tear_degree === "3c" || form.perineal_tear_degree === "4") {
            list.push({
                type: "warning",
                title: "Severe Perineal Tear",
                detail: "Grade " + form.perineal_tear_degree + " perineal tear",
                action: "Refer to experienced surgeon for repair. Antibiotics.",
            });
        }

        /* AMTSL not completed */
        if (!amtslComplete && form.delivery_mode) {
            list.push({
                type: "info",
                title: "AMTSL Incomplete",
                detail: "Active Management of Third Stage of Labour not fully completed",
                action: "Complete all AMTSL steps to reduce PPH risk.",
            });
        }

        return list;
    }, [pphAlert, form, amtslComplete]);

    /* ─── Is operative delivery ─── */
    const isOperative = useMemo(function() {
        return form.delivery_mode === "emergency_cs" ||
               form.delivery_mode === "elective_cs" ||
               form.delivery_mode === "vacuum" ||
               form.delivery_mode === "forceps";
    }, [form.delivery_mode]);

    const isCS = useMemo(function() {
        return form.delivery_mode === "emergency_cs" || form.delivery_mode === "elective_cs";
    }, [form.delivery_mode]);

    /* ─── Save delivery record ─── */
    const saveDelivery = useCallback(function() {
        if (!form.delivery_mode) {
            actions.snackbarActions.snackbarOpen("error", "Please select delivery mode");
            return;
        }
        if (!form.delivery_datetime) {
            actions.snackbarActions.snackbarOpen("error", "Please enter delivery date/time");
            return;
        }
        setSaving(true);
        const payload = Object.assign({}, form, {
            labour_admission_id: admission ? admission.id : null,
            booking_id: booking ? booking.id : null,
            amtsl_completed: amtslComplete,
        });

        if (USE_MOCK_DATA) {
            setTimeout(function() {
                const saved = Object.assign({}, payload, { id: deliveryRecord ? deliveryRecord.id : Date.now() });
                setDeliveryRecord(saved);
                actions.snackbarActions.snackbarOpen("success",
                    deliveryRecord ? "Delivery record updated (mock)" : "Delivery record saved (mock)");
                setSaving(false);
                setActiveTab("summary");
            }, 300);
            return;
        }

        const request = deliveryRecord
            ? axios.put(config.smsUrl + "/obgyn/delivery/" + deliveryRecord.id, payload)
            : axios.post(config.smsUrl + "/obgyn/delivery", payload);

        request
            .then(function(res) {
                const data = res.data || payload;
                setDeliveryRecord(data);
                actions.snackbarActions.snackbarOpen("success",
                    deliveryRecord ? "Delivery record updated" : "Delivery record saved");
                setActiveTab("summary");
            })
            .catch(function(err) {
                const msg = (err.response && err.response.data && err.response.data.message) ||
                    "Failed to save delivery record";
                actions.snackbarActions.snackbarOpen("error", msg);
            })
            .finally(function() { setSaving(false); });
    }, [form, admission, booking, amtslComplete, deliveryRecord, actions, setActiveTab]);

    /* ─── Summary ─── */
    const summary = useMemo(function() {
        if (!deliveryRecord) return null;
        const modeLabel = DELIVERY_MODES.find(function(m) { return m.value === deliveryRecord.delivery_mode; });
        return {
            mode: modeLabel ? modeLabel.label : deliveryRecord.delivery_mode,
            datetime: deliveryRecord.delivery_datetime,
            bloodLoss: deliveryRecord.estimated_blood_loss_ml || "—",
            babies: deliveryRecord.number_of_babies || 1,
            condition: deliveryRecord.maternal_condition_post_delivery || "—",
            complications: (deliveryRecord.complications || []).length,
            amtsl: deliveryRecord.amtsl_completed ? "Complete" : "Incomplete",
        };
    }, [deliveryRecord]);

    return {
        /* Constants */
        DELIVERY_MODES: DELIVERY_MODES,
        PLACENTA_METHODS: PLACENTA_METHODS,
        EPISIOTOMY_TYPES: EPISIOTOMY_TYPES,
        TEAR_DEGREES: TEAR_DEGREES,
        UTEROTONICS: UTEROTONICS,
        CORD_CLAMPING: CORD_CLAMPING,
        MATERNAL_CONDITIONS: MATERNAL_CONDITIONS,
        COMPLICATION_OPTIONS: COMPLICATION_OPTIONS,
        AMTSL_ITEMS: AMTSL_ITEMS,
        /* Data */
        admission: admission,
        booking: booking,
        deliveryRecord: deliveryRecord,
        newborns: newborns,
        setNewborns: setNewborns,
        /* Form */
        form: form,
        handleChange: handleChange,
        toggleComplication: toggleComplication,
        amtslChecklist: amtslChecklist,
        toggleAmtsl: toggleAmtsl,
        amtslComplete: amtslComplete,
        /* Computed */
        isOperative: isOperative,
        isCS: isCS,
        pphAlert: pphAlert,
        alerts: alerts,
        summary: summary,
        /* Actions */
        saveDelivery: saveDelivery,
        /* UI */
        loading: loading,
        saving: saving,
        activeTab: activeTab,
        setActiveTab: setActiveTab,
        activeSection: activeSection,
        setActiveSection: setActiveSection,
    };
};
